import { TablePagination, TablePaginationProps, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Action, ActionAnchoring, AtonUnitDetails } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import PaddedBox from "~/components/PaddedBox";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";

interface Props {
  atonData: AtonUnitDetails;
  small?: boolean;
}

type AnchoringAction = ActionAnchoring & Action;

const AtoNActionsTab = ({ atonData, small }: Props): JSX.Element => {
  const { t } = useTranslation("aton-anchoring-action");
  const dateFormatter = useDateFormatter();
  const theme = useTheme();

  const { actions } = atonData;
  const anchoringActions = React.useMemo(() => {
    const anch: AnchoringAction[] = [];
    actions.forEach((a) => {
      if (a.anchoring) anch.push({ ...a.anchoring, ...a });
    });
    return anch;
  }, [actions]);

  const columns: CustomColumn<AnchoringAction>[] = React.useMemo(
    () => [
      {
        title: t("date"),
        field: "timestamp",
        render: ({ timestamp }) =>
          timestamp ? dateFormatter({ date: timestamp }) : "",
      },
      {
        title: t("buoyCondition"),
        field: "buoyCondition",
      },
      {
        title: t("chainCondition"),
        field: "chainCondition",
      },
      {
        title: t("buoyCondition"),
        field: "buoyCondition",
      },
      {
        title: t("topAttachment"),
        field: "fasteningTopCondition",
      },
      {
        title: t("bottomAttachment"),
        field: "fasteningBottomCondition",
      },
      {
        title: t("anchoring"),
        field: "anchoringCondition",
      },
      {
        title: t("information"),
        field: "memo",
      },
    ],
    [dateFormatter, t],
  );

  const tableProps: CustomTableProps<AnchoringAction> = React.useMemo(
    () => ({
      title: t("aton:list-anchoring-actions"),
      ...(small
        ? {
            options: {
              pageSizeOptions: [5, 10, 15, 25, 50],
              pageSize: 10,
            },
            components: {
              Container: (props) => (
                <div
                  {...props}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    border: "none",
                  }}
                />
              ),
              Pagination: (props: TablePaginationProps) => {
                const { style } = props;
                return (
                  <TablePagination
                    {...props}
                    style={{
                      borderBottomRightRadius: theme.shape.borderRadius,
                      ...style,
                    }}
                  />
                );
              },
            },
          }
        : {}),
    }),
    [small, t, theme.shape.borderRadius],
  );

  const table = React.useMemo(
    () => (
      <GenericTable
        data={anchoringActions ?? []}
        columns={columns}
        tableProps={tableProps}
      />
    ),
    [anchoringActions, columns, tableProps],
  );

  return small ? table : <PaddedBox>{table}</PaddedBox>;
};

export default AtoNActionsTab;
